import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, User, Target, Brain, Clock, BarChart3, ArrowRight, Play } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/* ─── Chat demo data ─────────────────────────────────────────────── */
const features = [
  { icon: <Target size={20} color="#fff" />, title: 'Lead Qualification',   desc: 'Automatically qualifies leads and captures contact info', gradA: '#479EC3', gradB: '#22D3EE' },
  { icon: <Brain   size={20} color="#fff" />, title: 'Smart Responses',       desc: 'AI learns from your business to provide accurate answers',  gradA: '#22D3EE', gradB: '#479EC3' },
  { icon: <Clock   size={20} color="#fff" />, title: '24/7 Availability',     desc: 'Never miss a customer inquiry, day or night',               gradA: '#479EC3', gradB: '#22D3EE' },
  { icon: <BarChart3 size={20} color="#fff" />, title: 'Analytics Dashboard', desc: 'Track conversations, conversions, and customer insights',    gradA: '#22D3EE', gradB: '#479EC3' },
];

const initialMessages = [
  { id: 1, type: 'customer', content: "Hi! Do you have a table for 2 this Friday evening?", timestamp: '6:14 PM' },
];

const followUps = [
  { id: 2, delay: 200,  type: 'ai',       content: "Yes! 7:00 PM and 8:30 PM are available. Any preference?", timestamp: '6:14 PM' },
  { id: 3, delay: 1000, type: 'customer', content: "7pm works great.", timestamp: '6:15 PM' },
  { id: 4, delay: 1800, type: 'ai',       content: "May I have a name for the reservation?", timestamp: '6:15 PM' },
  { id: 5, delay: 2600, type: 'customer', content: "It's under Emma.", timestamp: '6:15 PM' },
  { id: 6, delay: 3400, type: 'ai',       content: "✓ Done! Table for 2, Friday at 7 PM under Emma. See you then!", timestamp: '6:15 PM', isLeadConverted: true },
];

const logos = ['Acme Corp', 'Stripe', 'Linear', 'Vercel', 'Notion'];

/* ─── Chat Panel ─────────────────────────────────────────────────── */
function ChatPanel() {
  const [messages, setMessages] = useState(initialMessages);
  const ref = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let timers = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          timers = followUps.map(({ delay, ...msg }) =>
            setTimeout(() => setMessages(prev => [...prev, msg]), delay)
          );
        }
      },
      { threshold: 0.6 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { observer.disconnect(); timers.forEach(clearTimeout); };
  }, []);

  return (
    <div ref={ref} style={{
      background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(24px)',
      border: '1px solid rgba(30,41,59,0.5)', borderRadius: 12, padding: 24,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ padding: 8, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', borderRadius: 8, display: 'flex' }}>
          <Bot size={20} color="#fff" />
        </div>
        <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>Pulse by Reano</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <motion.div animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
            style={{ width: 8, height: 8, background: '#22D3EE', borderRadius: '50%' }} />
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Live</span>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="chat-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 14, height: 420, overflowY: 'auto', paddingRight: 4, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <AnimatePresence>
          {messages.map(msg => (
            <motion.div key={msg.id}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{ display: 'flex', flexDirection: msg.type === 'customer' ? 'row-reverse' : 'row', gap: 10 }}>

              <div style={{
                flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
                background: msg.type === 'customer' ? '#334155' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {msg.type === 'customer' ? <User size={14} color="#fff" /> : <Bot size={14} color="#fff" />}
              </div>

              <div style={{ flex: 1, maxWidth: '80%' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: 14,
                  background: msg.type === 'customer'
                    ? 'rgba(30,41,59,0.6)'
                    : msg.isLeadConverted
                    ? 'linear-gradient(135deg,rgba(16,185,129,0.15),rgba(34,211,238,0.15))'
                    : 'linear-gradient(135deg,rgba(71,158,195,0.15),rgba(34,211,238,0.15))',
                  border: msg.type === 'customer'
                    ? '1px solid rgba(51,65,85,0.5)'
                    : msg.isLeadConverted
                    ? '1px solid rgba(16,185,129,0.4)'
                    : '1px solid rgba(71,158,195,0.3)',
                }}>
                  <p style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, margin: 0 }}>{msg.content}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: '#64748b' }}>{msg.timestamp}</span>
                    {msg.isLeadConverted && (
                      <span style={{ fontSize: 10, color: '#34d399', fontWeight: 600 }}>Booking Confirmed</span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Main Hero ───────────────────────────────────────────────────── */
export default function Hero() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <section style={{ position: 'relative', overflow: 'hidden', paddingTop: 80, paddingBottom: 80 }}>
      {/* Grid bg */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.12,
        backgroundImage: 'linear-gradient(to right,#1e293b 1px,transparent 1px),linear-gradient(to bottom,#1e293b 1px,transparent 1px)',
        backgroundSize: '64px 64px', pointerEvents: 'none',
      }} />

      {/* Glow orbs */}
      <motion.div animate={{ opacity: [0.15, 0.28, 0.15] }} transition={{ duration: 4, repeat: Infinity }}
        style={{ position: 'absolute', top: 0, left: '10%', width: 400, height: 400, pointerEvents: 'none', willChange: 'opacity' }}>
        <div style={{ width: '100%', height: '100%', background: '#479EC3', borderRadius: '50%', filter: 'blur(140px)' }} />
      </motion.div>
      <motion.div animate={{ opacity: [0.1, 0.22, 0.1] }} transition={{ duration: 4, repeat: Infinity, delay: 1.5 }}
        style={{ position: 'absolute', top: 100, right: '5%', width: 350, height: 350, pointerEvents: 'none', willChange: 'opacity' }}>
        <div style={{ width: '100%', height: '100%', background: '#22D3EE', borderRadius: '50%', filter: 'blur(140px)' }} />
      </motion.div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

        {/* Headline */}
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          style={{ textAlign: 'center', fontSize: 'clamp(36px, 5vw, 68px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 20, letterSpacing: '-0.02em' }}>
           An AI Assistant That{' '}
          <span style={{ background: 'linear-gradient(135deg,#479EC3,#22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Truly Understands
          </span>{' '}
          Your Business
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          style={{ textAlign: 'center', fontSize: 'clamp(16px, 2vw, 20px)', color: '#94a3b8', maxWidth: 640, margin: '0 auto 36px', lineHeight: 1.6 }}>
          Train AI on your docs, support history, FAQs, and workflows. Deploy intelligent AI agents in minutes — no code required.
        </motion.p>

        {/* CTAs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 52 }}>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate(user ? '/dashboard' : '/auth')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '14px 28px',
              background: 'linear-gradient(135deg,#479EC3,#22D3EE)', border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 0 32px rgba(34,211,238,0.25)',
            }}>
            Start Free <ArrowRight size={18} />
          </motion.button>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
            onClick={() => {
              const el = document.querySelector('#contact-form');
              if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior: 'smooth' });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '14px 28px',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
              color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            }}>
            <Play size={16} fill="#fff" /> Book a Demo
          </motion.button>
        </motion.div>

        {/* Social proof */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.45 }}
          style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ display: 'flex', gap: 32, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
            {[['5x', 'Quicker Responses'], ['3x', 'More Leads Captured'], ['80%', 'Lower Support Costs']].map(([num, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{num}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dashboard preview */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
          style={{
            position: 'relative', background: 'linear-gradient(135deg,#020617,#0f172a,#020617)',
            borderRadius: 16, overflow: 'hidden', padding: '32px',
            border: '1px solid rgba(71,158,195,0.2)',
            boxShadow: '0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(71,158,195,0.08)',
          }}>
          <div className="pulse-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <ChatPanel />

            {/* Feature cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ marginBottom: 4 }}>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Powerful Features</h3>
                <p style={{ fontSize: 13, color: '#64748b' }}>Everything you need to convert visitors into customers</p>
              </div>
              {features.map((f, i) => (
                <motion.div key={f.title}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  whileHover={{ borderColor: 'rgba(71,158,195,0.5)', boxShadow: '0 0 20px rgba(71,158,195,0.1)' }}
                  style={{
                    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(30,41,59,0.5)', borderRadius: 12, padding: 16,
                    transition: 'border-color 0.3s, box-shadow 0.3s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ padding: 10, background: `linear-gradient(135deg,${f.gradA},${f.gradB})`, borderRadius: 8, flexShrink: 0, display: 'flex' }}>
                      {f.icon}
                    </div>
                    <div>
                      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{f.title}</h4>
                      <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5, margin: 0 }}>{f.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        .chat-scroll::-webkit-scrollbar { display: none; }
        @media (max-width: 860px) {
          .pulse-hero-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
